import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PROJECT_CATEGORY_LABELS,
  PROJECT_STATUS,
  PROJECT_STATUS_LABELS,
  PROJECT_TYPE_LABELS,
} from '@/constants/project';
import { ROUTES } from '@/constants/routes';
import { ProjectDepartmentMigrationModal } from '@/pages/admin/projects/ProjectDepartmentMigrationModal';
import { ApiError } from '@/services/api/client';
import * as projectsApi from '@/services/api/projects';
import type { ProjectListItem, ProjectStatus } from '@/types/project';
import { formatDate } from '@/utils/format-date';
import { formatWanYuan } from '@/utils/format-yuan';

interface FilterFormValues {
  keyword?: string;
}

function isPendingMigration(project: ProjectListItem): boolean {
  return project.departmentsConfigured === false;
}

function renderLedgerText(value: string | null | undefined, rows = 3) {
  const text = value?.trim() || '-';
  return (
    <Typography.Paragraph
      style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
      ellipsis={text === '-' ? false : { rows, tooltip: text }}
    >
      {text}
    </Typography.Paragraph>
  );
}

export function ProjectListPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [filterForm] = Form.useForm<FilterFormValues>();

  const [statusTab, setStatusTab] = useState<ProjectStatus>(PROJECT_STATUS.ACTIVE);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ProjectListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<FilterFormValues>({});

  const [migrationOpen, setMigrationOpen] = useState(false);
  const [migrationProject, setMigrationProject] = useState<ProjectListItem | null>(null);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const result = await projectsApi.fetchProjects({
        page,
        pageSize,
        keyword: filters.keyword?.trim() || undefined,
        status: statusTab,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载项目列表失败';
      message.error(text);
    } finally {
      setLoading(false);
    }
  }, [filters, message, page, pageSize, statusTab]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  const pendingCount = useMemo(
    () => items.filter((item) => isPendingMigration(item)).length,
    [items],
  );

  const handleSearch = (values: FilterFormValues) => {
    setFilters(values);
    setPage(1);
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setPage(pagination.current ?? 1);
    setPageSize(pagination.pageSize ?? 20);
  };

  const openMigration = (record: ProjectListItem) => {
    setMigrationProject(record);
    setMigrationOpen(true);
  };

  const columns: ColumnsType<ProjectListItem> = [
    {
      title: '序号',
      width: 70,
      align: 'center',
      fixed: 'left',
      render: (_: unknown, __: ProjectListItem, index: number) => (page - 1) * pageSize + index + 1,
    },
    {
      title: '项目分类',
      dataIndex: 'projectCategories',
      width: 220,
      fixed: 'left',
      render: (categories: ProjectListItem['projectCategories'] = [], record) =>
        categories.length > 0 ? (
          <Space size={[4, 4]} wrap>
            {categories.map((category) => (
              <Tag key={category}>{PROJECT_CATEGORY_LABELS[category]}</Tag>
            ))}
          </Space>
        ) : (
          <Tag>{PROJECT_TYPE_LABELS[record.projectType] ?? '未分类'}</Tag>
        ),
    },
    {
      title: '项目名称',
      dataIndex: 'name',
      width: 220,
      fixed: 'left',
      render: (value: string, record) => (
        <div style={{ minWidth: 0, width: '100%' }}>
          <Link
            to={ROUTES.admin.projectDetail(record.id)}
            title={value}
            style={{
              display: 'block',
              lineHeight: 1.5,
              overflowWrap: 'anywhere',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {value}
          </Link>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {record.projectNo}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: '建设单位',
      dataIndex: 'constructionUnit',
      width: 180,
      render: (value: string | null) => renderLedgerText(value, 2),
    },
    {
      title: '总投资',
      dataIndex: 'totalInvestment',
      width: 170,
      align: 'right',
      render: (value: string | null) => formatWanYuan(value),
    },
    {
      title: '年度推进计划',
      width: 300,
      render: (_: unknown, record) => (
        <div>
          {record.annualPlanYear ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {record.annualPlanYear}年
            </Typography.Text>
          ) : null}
          {renderLedgerText(record.annualPlan)}
        </div>
      ),
    },
    {
      title: '本周进展（周报）',
      width: 320,
      render: (_: unknown, record) => (
        <div>
          {record.latestWeeklyReport ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {formatDate(record.latestWeeklyReport.reportDate)}
            </Typography.Text>
          ) : null}
          {renderLedgerText(record.latestWeeklyReport?.progressContent)}
        </div>
      ),
    },
    {
      title: '存在问题',
      width: 240,
      render: (_: unknown, record) => renderLedgerText(record.latestWeeklyReport?.problemContent),
    },
    {
      title: '负责人',
      dataIndex: ['projectManager', 'name'],
      width: 120,
      render: (_: unknown, record) => record.projectManager?.name ?? '-',
    },
    {
      title: '备注',
      dataIndex: 'remarks',
      width: 180,
      render: (value: string | null) => renderLedgerText(value, 2),
    },
    {
      title: '部门配置',
      width: 100,
      render: (_: unknown, record) =>
        isPendingMigration(record) ? (
          <Tag color="warning">待迁移</Tag>
        ) : (
          <Tag color="success">已配置</Tag>
        ),
    },
    {
      title: '项目状态',
      dataIndex: 'status',
      width: 100,
      render: (value: ProjectStatus) => (
        <Tag color={value === PROJECT_STATUS.ACTIVE ? 'processing' : 'default'}>
          {PROJECT_STATUS_LABELS[value]}
        </Tag>
      ),
    },
    {
      title: '操作',
      width: 140,
      fixed: 'right',
      render: (_: unknown, record) => (
        <Space size={0} wrap>
          <Button
            type="link"
            size="small"
            onClick={() => navigate(ROUTES.admin.projectDetail(record.id))}
          >
            详情
          </Button>
          {isPendingMigration(record) ? (
            <Button type="link" size="small" onClick={() => openMigration(record)}>
              配置部门
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        项目管理
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        项目台账自动展示负责人提交的最新周报；历史项目需先完成部门迁移确认。
      </Typography.Paragraph>

      {statusTab === PROJECT_STATUS.ACTIVE && pendingCount > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`当前页有 ${pendingCount} 个项目待配置项目部门`}
          description="请使用「配置部门」根据系统建议确认部门范围，确认后将自动同步项目成员。"
        />
      ) : null}

      <Card style={{ marginBottom: 16 }}>
        <Tabs
          activeKey={statusTab}
          onChange={(key) => {
            setStatusTab(key as ProjectStatus);
            setPage(1);
          }}
          items={[
            { key: PROJECT_STATUS.ACTIVE, label: '进行中' },
            { key: PROJECT_STATUS.ARCHIVED, label: '归档库' },
          ]}
        />
        <Form form={filterForm} layout="inline" onFinish={handleSearch} style={{ marginTop: 8 }}>
          <Form.Item name="keyword" label="关键字">
            <Input allowClear placeholder="项目编号 / 名称 / 建设单位" style={{ width: 280 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                查询
              </Button>
              <Button
                onClick={() => {
                  filterForm.resetFields();
                  setFilters({});
                  setPage(1);
                }}
              >
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card
        title="项目列表"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(ROUTES.admin.projectCreate)}>
            创建项目
          </Button>
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={items}
          tableLayout="fixed"
          scroll={{ x: 2450 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      <ProjectDepartmentMigrationModal
        open={migrationOpen}
        project={migrationProject}
        onClose={() => {
          setMigrationOpen(false);
          setMigrationProject(null);
        }}
        onConfirmed={() => void loadProjects()}
      />
    </div>
  );
}
